// Agent journey card — where the WhatsApp confirmation agent's conversation
// for this order has got to, and a switch to take the order away from it.
//
// Task 12 of the WhatsApp confirmation agent slice (2026-08-13). Sits on
// AdminOrderDetailPage near AmeexDeliveryCard — same card shell, same French
// tone, same "label + checkbox" toggle shape as that card's "Payé
// intégralement" pill.
//
// GATING: both the read (GET /whatsapp/journeys/:orderId) and the write
// (POST .../handover) are gated on `whatsapp.manage`, which today is granted
// to NO role — see worker/src/routes/whatsapp.ts's comment on the handover
// route. Only a full admin (who bypasses permission checks entirely) can see
// or use this card right now. `usePermissions()` mirrors that boundary
// client-side — same permission AdminLayout.tsx already uses to hide the
// "Assistant WhatsApp" nav item — so the card renders NOTHING for anyone who
// would just get a 403 from the query. That is the "cheaply hide the
// control" half of the brief. The `isError` branch below is what's left for
// the other half: a stale permission cache, a revoked role mid-session, or
// any other way a gated request can still fail live.
//
// REAL STATE, NOT OPTIMISTIC: the checkbox is fully controlled by
// `journey.paused_reason` as last read from the server. Clicking it fires
// the mutation and nothing else — there is no local "assume it worked" flag.
// `onSettled` always refetches, on success AND on failure, so a failed
// request (network blip, an expired session, a stale permission) leaves the
// switch exactly where the server says it is. An admin who believes the bot
// is paused when it isn't would be blindsided by the next message going
// out — this is the one behaviour the brief calls out by name, so it is not
// a nice-to-have.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { whatsappApi, type AgentJourneyState } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { usePermissions } from '@/lib/permissionsStore';

// Human French labels for worker/src/lib/agentJourney.ts's `JourneyState` —
// an admin should never have to read `awaiting_client_ok` to know what's
// happening. Kept in the same order as the worker's type for easy diffing.
const STATE_LABELS: Record<AgentJourneyState, string> = {
  opened: 'Conversation démarrée',
  awaiting_destination: 'En attente de la destination du client',
  awaiting_cover: 'Client en train de choisir sa couverture',
  collecting_photos: 'Réception des photos en cours',
  confirming_batch: 'En attente de confirmation du lot de photos',
  downloading: 'Téléchargement des photos',
  building: 'Construction de l’album',
  previewing: 'Génération de l’aperçu',
  awaiting_client_ok: 'En attente de validation de l’aperçu par le client',
  awaiting_payment: 'En attente du paiement',
  awaiting_receipt: 'Reçu de paiement reçu — vérification en cours',
  ready_for_human: 'Bloqué — nécessite une intervention humaine',
  done: 'Conversation terminée',
};

export function AgentJourneyCard({ orderId }: { orderId: string }) {
  const { t } = useT();
  const { can } = usePermissions();
  const canManage = can('whatsapp.manage');
  const queryClient = useQueryClient();
  const queryKey = ['agent-journey', orderId];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => whatsappApi.journey(orderId),
    enabled: canManage,
    retry: false,
  });

  const [actionError, setActionError] = useState<string | null>(null);

  const handover = useMutation({
    mutationFn: (paused: boolean) => whatsappApi.handover(orderId, paused),
    onMutate: () => setActionError(null),
    onError: (e: Error & { status?: number }) => {
      setActionError(
        e.status === 403
          ? t('Action refusée — vous n’avez pas la permission de gérer l’assistant WhatsApp.')
          : t('L’action a échoué : {message}', { message: e.message }),
      );
    },
    // Refetch unconditionally, success or failure. Never trust the click —
    // the checkbox below only ever renders what this query says.
    onSettled: () => { void queryClient.invalidateQueries({ queryKey }); },
  });

  // No permission → not even a query fires. Same UX as a customer support
  // agent not seeing "Assistant WhatsApp" in the nav: not a broken control,
  // simply not there.
  if (!canManage) return null;

  // While loading, render nothing rather than a skeleton — the common case
  // (no journey on this order) also renders nothing, so a skeleton would
  // flash-then-vanish for most orders every time the page opens.
  if (isLoading) return null;

  if (isError) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <p className="text-xs text-red-600">
          {t('Agent WhatsApp : impossible de charger l’état de la conversation.')}
        </p>
      </div>
    );
  }

  const journey = data?.journey ?? null;
  // Most orders have no confirmation-agent conversation at all (pilot phase,
  // and Solo-only). Quiet empty state, exactly as the brief asks for.
  if (!journey) return null;

  const outbox = data?.outbox ?? { queued: 0, failed: 0 };
  const paused = journey.paused_reason != null;
  const stuck = outbox.failed > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center bg-[linear-gradient(135deg,#F3E6BC_0%,#C9A24A_100%)] dark:bg-[linear-gradient(135deg,#6E561E_0%,#C9A24A_100%)]"
          >
            <span className="text-white text-sm">🤖</span>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">{t('Agent WhatsApp')}</h2>
            <p className="text-xs text-gray-500">{t(STATE_LABELS[journey.state])}</p>
          </div>
        </div>
        {paused && (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-800">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            {t('Confié à un humain')}
          </span>
        )}
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <SummaryItem label={t('Photos reçues')} value={String(journey.photo_count)} />
        <SummaryItem
          label={t('Dernier message client')}
          value={journey.last_inbound_at ? formatDate(journey.last_inbound_at) : '—'}
        />
        <SummaryItem label={t('En file d’attente')} value={String(outbox.queued)} />
        <SummaryItem
          label={t('Échecs d’envoi')}
          value={String(outbox.failed)}
          tone={stuck ? 'danger' : undefined}
        />
      </div>

      {/* ⚠️ THE REASON, NOT A GUESS. This said « la conversation est peut-être
          bloquée » for every failure — so on #1186, where the CUSTOMER's number
          simply is not on WhatsApp, it sent the reader to check our own line.
          The gateway had already said what was wrong; the worker now classifies
          it (lib/sendFailure.ts) and we print that. The old sentence survives
          only for the case it was ever true of: nothing to go on. */}
      {stuck && (
        <p className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          <span className="font-semibold">
            {t('{n} message(s) non envoyé(s).', { n: outbox.failed })}
          </span>{' '}
          {outbox.failure_reason
            ?? t('Plusieurs tentatives ont échoué et aucune raison n’a été enregistrée.')}
        </p>
      )}

      <label
        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
          paused ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white hover:bg-gray-50'
        } ${handover.isPending ? 'opacity-60 pointer-events-none' : ''}`}
      >
        <input
          type="checkbox"
          checked={paused}
          onChange={(e) => handover.mutate(e.target.checked)}
          disabled={handover.isPending}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
        />
        <div className="flex-1 text-sm">
          <div className={`font-semibold ${paused ? 'text-amber-900' : 'text-gray-800'}`}>
            {t('Confier à un humain')}
          </div>
          <div className={`text-xs mt-0.5 ${paused ? 'text-amber-800' : 'text-gray-500'}`}>
            {paused
              ? t(
                  'L’IA ne touchera plus cette commande. Décochez pour lui rendre la conversation — elle reprendra exactement où elle s’est arrêtée, pas depuis le début.',
                )
              : t('L’assistant WhatsApp gère cette conversation automatiquement. Cochez pour la lui retirer.')}
          </div>
        </div>
      </label>

      {actionError && <p className="text-xs text-red-600">{actionError}</p>}
    </div>
  );
}

function SummaryItem({
  label, value, tone,
}: { label: string; value: string; tone?: 'danger' }) {
  return (
    <div>
      <dt className="text-xs text-gray-500 mb-0.5">{label}</dt>
      <dd className={`text-sm font-medium ${tone === 'danger' ? 'text-rose-700' : 'text-gray-800'}`}>{value}</dd>
    </div>
  );
}
